import breakpoints from './platform-breakpoints.json';

/**
 * Tailwind-aligned min-width breakpoint names (default/unprefixed viewport is {@link BaseScreenSizeCategory}).
 */
export type TailwindBreakpointName = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/**
 * Viewport bucket aligned with Tailwind responsive prefixes (`base` is below `sm`).
 */
export type ScreenSizeCategory = 'base' | TailwindBreakpointName;

/** Canonical min-width pixel values shared by Tailwind, SCSS, and {@link PlatformStore}. */
export const SCREEN_BREAKPOINTS: Readonly<
  Record<TailwindBreakpointName, number>
> = breakpoints;

/** PrimeNG Carousel / Galleria `responsiveOptions` breakpoint strings (max-width semantics). */
export const SCREEN_BREAKPOINTS_PX: Readonly<
  Record<TailwindBreakpointName, `${number}px`>
> = {
  sm: `${SCREEN_BREAKPOINTS.sm}px`,
  md: `${SCREEN_BREAKPOINTS.md}px`,
  lg: `${SCREEN_BREAKPOINTS.lg}px`,
  xl: `${SCREEN_BREAKPOINTS.xl}px`,
  '2xl': `${SCREEN_BREAKPOINTS['2xl']}px`,
};

const BREAKPOINT_ORDER: readonly TailwindBreakpointName[] = [
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
];

/**
 * Maps viewport width to the active Tailwind-aligned screen-size category.
 *
 * Uses min-width semantics: width 576 → `sm`, width 575 → `base`.
 *
 * @param width - Viewport width in pixels.
 * @returns The {@link ScreenSizeCategory} for the width.
 * @example
 * getScreenSizeCategory(576) // => 'sm'
 * getScreenSizeCategory(575) // => 'base'
 */
export const getScreenSizeCategory = (width: number): ScreenSizeCategory => {
  for (let index = BREAKPOINT_ORDER.length - 1; index >= 0; index--) {
    const name = BREAKPOINT_ORDER[index];
    if (width >= SCREEN_BREAKPOINTS[name]) {
      return name;
    }
  }
  return 'base';
};

/**
 * True when viewport width is strictly below a breakpoint min-width (Tailwind `max-*` semantics).
 *
 * @param width - Viewport width in pixels.
 * @param breakpoint - Tailwind breakpoint name (`sm` → < 576px is false at 576).
 * @example
 * isBelowBreakpoint(575, 'sm') // => true
 * isBelowBreakpoint(576, 'sm') // => false
 */
export const isBelowBreakpoint = (
  width: number,
  breakpoint: TailwindBreakpointName
): boolean => width < SCREEN_BREAKPOINTS[breakpoint];

/**
 * App-shell compact viewport — below `lg` (< 992px), matching Sakai mobile sidebar split.
 *
 * @param width - Viewport width in pixels.
 */
export const isSmallScreenWidth = (width: number): boolean =>
  isBelowBreakpoint(width, 'lg');
